import LinkButton from '@atlaskit/button/link';
import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';
import {
	type FrontendPlacementMessage,
	type AnyFrontendStage,
} from '@post-office/message-maker-browser/types';

import { useCallback } from 'react';

import { noValidation } from '@post-office/message-maker-browser/message-helpers';

import { FormattedMessage, defineMessages } from 'react-intl';

import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';

import { Text } from '@atlaskit/primitives/text';

import { Stack } from '@atlaskit/primitives/stack';

import {
	PlacementFrontend,
	type placementRenderProps,
} from '@post-office/browser--jsm-phantom/jira-space-settings-nav-playbooks';

import Link from '@atlaskit/link/link';

import type { CreatePlacementMessageConfig } from '@post-office/message-template-contracts/placement-v2';

import { SpotlightSecondaryAction } from '@atlaskit/spotlight/secondary-action';

import { SpotlightHeadline } from '@atlaskit/spotlight/headline';

import { SpotlightHeader } from '@atlaskit/spotlight/header';

import { SpotlightFooter } from '@atlaskit/spotlight/footer';

import { SpotlightDismissControl } from '@atlaskit/spotlight/dismiss-control';

import { SpotlightControls } from '@atlaskit/spotlight/controls';

import { SpotlightCard } from '@atlaskit/spotlight/card';

import { SpotlightBody } from '@atlaskit/spotlight/body';

import { SpotlightActions } from '@atlaskit/spotlight/actions';

import { PopoverTarget } from '@atlaskit/spotlight/popover-target';

import { PopoverProvider } from '@atlaskit/spotlight/popover-provider';

import { PopoverContent } from '@atlaskit/spotlight/popover-content';

const messages = defineMessages({
	heading: {
		id: 'post-office.jsm-phantom.space-settings.playbook-nav.spotlight-heading',
		defaultMessage: 'Turn your team’s knowledge into playbooks',
		description: 'Heading for playbook spotlight.',
	},
	firstLine: {
		id: 'post-office.jsm-phantom.space-settings.playbook-nav.spotlight-first-line',
		defaultMessage: `Create reusable playbooks from runbooks, SOPs, and how-tos to give your team step-by-step guidance and automated actions to resolve work items faster, right in context.`,
		description: 'First body line for playbook spotlight.',
	},
	learnMoreLink: {
		id: 'post-office.jsm-phantom.space-settings.playbook-nav.spotlight-learn-more',
		defaultMessage: `Learn more about playbooks`,
		description: 'Learn more link for playbook spotlight.',
	},
	createPlaybookAction: {
		id: 'post-office.jsm-phantom.space-settings.playbook-nav.spotlight.create-playbook',
		defaultMessage: 'Create playbook',
		description: 'Create playbook action for spotlight.',
	},
	closeButton: {
		id: 'post-office.jsm-phantom.space-settings.playbook-nav.spotlight-close-button',
		defaultMessage: 'Close',
		description: 'close button to dismiss spotlight.',
	},
});

// Frontend
const render = PlacementFrontend.createFrontendStage({
	type: 'render',

	validate: noValidation<placementRenderProps>(),

	Component({ dismiss, onPrimaryAction, navigateToLink }: placementRenderProps) {
		const { onClosed, onCtaActioned } = usePostOfficeUserInteractions();
		const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();

		const dismissMessage = useCallback(() => {
			void onClosed({ interactionIdentifier: 'dismiss' });
			dismiss();
		}, [onClosed, dismiss]);

		const handleDismiss = useCallback(() => {
			dismissMessage();
			fireAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'playbookSpaceSettingsSpotlightDismissClick',
			});
		}, [dismissMessage, fireAnalyticsEvent]);

		const handleOnCreateClick = useCallback(() => {
			fireAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'playbookSpaceSettingsSpotlightCreateClick',
			});
			void onCtaActioned({
				interactionIdentifier: 'createPlaybook',
				destinationUrl: navigateToLink,
			});
			onPrimaryAction?.();
			dismiss();
		}, [fireAnalyticsEvent, onCtaActioned, navigateToLink, onPrimaryAction, dismiss]);

		const onLinkClick = useCallback(() => {
			fireAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'playbookSpaceSettingsSpotlightLinkClick',
			});
		}, [fireAnalyticsEvent]);

		return (
			<PopoverProvider>
				<PopoverTarget>
					{/* dummy div, the spotlight positioning will be handled by CSS */}
					<div />
				</PopoverTarget>
				<PopoverContent dismiss={handleDismiss} placement="right-end" isVisible={true}>
					<SpotlightCard>
						<SpotlightHeader>
							<SpotlightHeadline>
								<FormattedMessage {...messages.heading} />
							</SpotlightHeadline>
							<SpotlightControls>
								<SpotlightDismissControl onClick={handleDismiss} />
							</SpotlightControls>
						</SpotlightHeader>
						<SpotlightBody>
							<Stack space="space.100">
								<Text>
									<FormattedMessage {...messages.firstLine} />{' '}
									<Link href={YOUTUBE_LINK} target="_blank" onClick={onLinkClick}>
										<FormattedMessage {...messages.learnMoreLink} />
									</Link>
								</Text>
							</Stack>
						</SpotlightBody>
						<SpotlightFooter>
							<SpotlightActions>
								<SpotlightSecondaryAction onClick={handleDismiss}>
									<FormattedMessage {...messages.closeButton} />
								</SpotlightSecondaryAction>
								<LinkButton
									appearance="primary"
									href={navigateToLink}
									onClick={handleOnCreateClick}
								>
									<FormattedMessage {...messages.createPlaybookAction} />
								</LinkButton>
							</SpotlightActions>
						</SpotlightFooter>
					</SpotlightCard>
				</PopoverContent>
			</PopoverProvider>
		);
	},
});

const YOUTUBE_LINK = 'https://www.youtube.com/watch?v=RPm7e5C0N-E';

export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: AnyFrontendStage;
}> = PlacementFrontend.createMessageFrontend({
	render,
});
